import { useMemo } from 'react';

import type { TimeUnit } from '@cast/shared-types';

import { GpuUtilizationBreakdownChartItem } from '../_types';
import { inferTimeUnitFromTimestamps } from '../_utils/inferTimeUnitFromTimestamps';

import { useWorkloadGpuUtilizationSummary } from './useWorkloadGpuUtilizationSummary';

type UseGpuUtilizationBreakdownChartDataResult = {
  data: GpuUtilizationBreakdownChartItem[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  timeUnit?: TimeUnit;
  step?: number;
};

export const useGpuUtilizationBreakdownChartData =
  (): UseGpuUtilizationBreakdownChartDataResult => {
    const { intervals, isLoading, error, refetch } =
      useWorkloadGpuUtilizationSummary();

    const data: GpuUtilizationBreakdownChartItem[] = useMemo(
      () =>
        intervals
          .filter((item) => item.utilization)
          .map((item) => ({
            timestamp: item.timestamp,
            gpuUtilization: item.utilization!.gpuPercent,
            gpuMemoryUtilization: item.utilization!.memoryPercent,
          })),
      [intervals]
    );

    const granularity = useMemo(
      () => inferTimeUnitFromTimestamps(data.map((item) => item.timestamp)),
      [data]
    );

    return {
      data,
      isLoading,
      isFetching: isLoading,
      error,
      refetch,
      timeUnit: granularity?.unit,
      step: granularity?.step,
    };
  };
